/**
 * Kanban board canvas frame — 6-column task board with drag-and-drop.
 *
 * Columns: Backlog | Todo | In Progress | Blocked | In Review | Done
 * One task list, every active project's, filtered per board: a board opens
 * on its frame's project (Home's on All) in its `content.default_view`, and
 * project chips under the title change the filter. A pick wins on this
 * board until the next project switch. Cards move by a pointer-driven drag
 * (`useKanbanCardDrag`) to any column, in their own project.
 *
 * On a surface that owns its chrome (the wall's Pinned tile) it is a calm
 * title with "Open board" over the open tasks by status
 * (`TaskboardStatusList`); a task opens the docked pocket. An opened window
 * keeps only its title and hands Display to its pill.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { cn } from "@/lib/cn";
import { openFrames } from "@/lib/open-frames";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import { isFrameInFocus } from "@/lib/task-focus-tile";
import { TASK_POCKET_LANE } from "@/lib/task-pocket-placement";
import { usePresentationStore } from "@/stores/presentation-store";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "../shared/frame-chrome-config";
import { FrameChromeTools } from "../shared/frame-chrome-tools";
import { FrameCalmTitle } from "../shared/frame-calm-title";
import {
  CARD_ROWS_INSET,
  CARD_TITLE_INSET,
} from "../shared/frame-content-padding";
import { useFrameChromeHidden } from "../shared/frame-renderer-context";
import { Icons } from "@/components/ui/icons";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { TaskPocketAnchored } from "@/components/panels/task-pocket-anchored";
import {
  useBoardProjectId,
  useBoardTasks,
  useTaskDisplayState,
  filterTasksForView,
  tasksMatching,
} from "./task-display-filters";
import { BoardProjectChips } from "./board-project-chips";
import { BoardProjectsContext } from "./board-projects-context";
import { useProjectStore } from "@/stores/project-store";
import {
  boardDefaultProjects,
  type TaskViewMode,
} from "@/stores/task-view-session";
import { useTasksStore } from "@/stores/tasks-store";
import type { TaskStatus } from "@/api/tasks";
import { KanbanColumn } from "./kanban-column";
import { KANBAN_COLUMNS } from "./kanban-columns";
import { DisplayPopover } from "./display-popover";
import { ListView } from "./list-view";
import { CalendarView } from "./calendar-view";
import { taskboardStatusGroups } from "./taskboard-status-groups";
import { TaskboardStatusList } from "./taskboard-status-list";
import { sortTaskBoardOrder } from "./task-order";
import type { Task } from "@/api/tasks";
import { useFrameSurface } from "../shared/use-frame-surface";
import { useWallTileQuery } from "@/components/shapes/shared/wall-tile-query-context";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";

/**
 * An opened window's view under its calm title, at the card's margins
 * (`--open-pad-*`), so every view starts at the title's inline margin: list
 * rows pad themselves by 0.5rem (`CARD_ROWS_INSET`) and kanban columns their
 * labels and cards by 0.375rem (`px-1.5`), so those bodies pad that much
 * less; the calendar draws to its own edges. The board's columns share its
 * width down to `KANBAN_COLUMN_MIN_W`; only a narrower window scrolls it
 * sideways, its bottom margin below the scrollbar, clear of the corners.
 */
const OPENED_VIEW_INSET = {
  board:
    "px-[calc(var(--open-pad-inline)-0.375rem)] pt-[0.75em] mb-[var(--open-pad-block-end)]",
  list: CARD_ROWS_INSET,
  calendar:
    "px-[var(--open-pad-inline)] pt-[0.75em] pb-[var(--open-pad-block-end)]",
} as const;

/** The task pocket's lane, read by the strip's right padding. */
const POCKET_LANE_STYLE = {
  "--task-pocket-lane": `${TASK_POCKET_LANE}px`,
} as CSSProperties;

/** The board's padding elsewhere (the canvas, a conversation embed). */
const BOARD_INSET =
  "p-3 [--denk-frame-content-padding-block-start:12px] [--denk-frame-content-padding-block-end:12px] [--denk-frame-content-padding-inline:12px]";

/** The view a board opens in (`content.default_view`), Board when unset. */
function boardDefaultView(value: unknown): TaskViewMode {
  return value === "list" || value === "calendar" ? value : "board";
}

export function TaskboardFrame({ id, data, selected }: FrameProps) {
  const t = useT(framesTaskboardMessages);
  const frameSurface = useFrameSurface(id);
  const chromeHidden = useFrameChromeHidden();
  // The wall's search narrows the tile; it is empty everywhere else.
  const query = useWallTileQuery();
  const cardRef = useRef<HTMLDivElement>(null);
  const display = useTaskDisplayState();
  const projects = useProjectStore((s) => s.projects);
  const projectId = useBoardProjectId(data);
  const defaultView = boardDefaultView(data.content?.default_view);
  const defaultProjects = projectId
    ? boardDefaultProjects(projectId, projects)
    : "all";
  // The Pinned tile keeps no board state: it shows the frame's defaults.
  const board = useTasksStore((s) => (chromeHidden ? undefined : s.boards[id]));
  const viewMode = board?.view ?? defaultView;
  // A picked project that was archived since falls back to the default.
  const picked = board?.projects;
  const shownProjects =
    picked === "all" || projects.some((project) => project.id === picked)
      ? (picked ?? defaultProjects)
      : defaultProjects;
  const hasBoard = board !== undefined;
  const loaded = useTasksStore((s) => s.loaded);
  const changeTaskStatus = useTasksStore((s) => s.changeTaskStatus);
  const fetchTasks = useTasksStore((s) => s.fetchTasks);
  const applyBoardDefaults = useTasksStore((s) => s.applyBoardDefaults);
  const setBoardProjects = useTasksStore((s) => s.setBoardProjects);
  // Applied again after a project switch clears every board.
  useEffect(() => {
    if (!chromeHidden && projectId)
      applyBoardDefaults(id, projectId, defaultView, defaultProjects);
  }, [
    chromeHidden,
    id,
    projectId,
    defaultView,
    defaultProjects,
    hasBoard,
    applyBoardDefaults,
  ]);
  // Read at effect time, so two boards mounting together fetch once.
  useEffect(() => {
    if (!loaded && !useTasksStore.getState().isLoading) void fetchTasks();
  }, [loaded, fetchTasks]);

  const tasks = useBoardTasks(shownProjects);
  const showsProjects = shownProjects === "all" && projects.length > 1;
  const orderedTasks = useMemo(
    () => sortTaskBoardOrder(filterTasksForView(tasks, display, "board")),
    [tasks, display],
  );
  // The Pinned tile: the board's own tasks, narrowed by the wall's search.
  const tileGroups = useMemo(
    () =>
      chromeHidden
        ? taskboardStatusGroups(tasksMatching(orderedTasks, query), query)
        : [],
    [chromeHidden, orderedTasks, query],
  );
  const tileRows = tileGroups.reduce(
    (count, group) => count + group.tasks.length,
    0,
  );

  const agentOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tasks) {
      if (t.assigned_agent_detail) {
        map.set(t.assigned_agent_detail.id, t.assigned_agent_detail.name);
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [tasks]);

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, typeof orderedTasks> = {
      backlog: [],
      todo: [],
      in_progress: [],
      blocked: [],
      in_review: [],
      done: [],
    };
    for (const task of orderedTasks) {
      const bucket = grouped[task.status];
      if (bucket) {
        bucket.push(task);
      }
    }
    return grouped;
  }, [orderedTasks]);

  const [isDragging, setIsDragging] = useState(false);
  const [dropStatus, setDropStatus] = useState<TaskStatus | null>(null);

  const handleDragStartTask = useCallback(() => setIsDragging(true), []);

  // A person may move a task to any column; the server explains a move it
  // refuses (e.g. done with open subtasks) in the store's error toast.
  const handleDragEndTask = useCallback(
    (task: Task, newStatus: TaskStatus | null) => {
      setIsDragging(false);
      setDropStatus(null);
      if (!newStatus) return;
      const current = useTasksStore
        .getState()
        .tasks.find((t) => t.id === task.id);
      // Same-column drop is a no-op.
      if ((current?.status ?? task.status) === newStatus) return;
      void changeTaskStatus(task.project, task.id, newStatus);
    },
    [changeTaskStatus],
  );

  const handleSelectTask = useCallback(
    (taskId: string) => {
      // The tile anchors no pocket: it docks at the top right.
      if (chromeHidden) {
        void openTaskDetails(taskId);
        return;
      }
      const pocket = useTaskPocketStore.getState();
      if (
        pocket.taskId === taskId &&
        pocket.anchor?.kind === "board" &&
        pocket.anchor.frameId === id
      )
        pocket.close();
      else void openTaskDetails(taskId, { boardFrameId: id });
    },
    [chromeHidden, id],
  );
  const displayConfig = useMemo<FrameChromeConfig>(
    () => ({
      tools: [
        {
          kind: "popover",
          id: "display",
          label: t("display"),
          icon: Icons.SlidersHorizontal,
          renderPopover: () => (
            <DisplayPopover boardId={id} agentOptions={agentOptions} />
          ),
        },
      ],
    }),
    [agentOptions, id, t],
  );
  // An opened window's pill hosts Display; the board keeps only its title.
  const opened = useFrameChromeConfig(displayConfig);

  const title = data.title?.trim() || t("boardTitleFallback");
  // A focused board keeps the task pocket's lane free at its right edge
  // while a task is open on it, so the pocket never covers a column.
  const inFocus = usePresentationStore((s) =>
    isFrameInFocus(id, s.presentation),
  );
  const pocketOpenHere = useTaskPocketStore(
    (s) =>
      s.taskId !== null &&
      s.anchor?.kind === "board" &&
      s.anchor.frameId === id,
  );

  return (
    <div
      ref={cardRef}
      className={cn(
        frameSurface.className,
        "relative flex h-full flex-col",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="taskboard-frame"
      data-task-board={id}
      {...frameSurface.attributes}
    >
      {chromeHidden ? (
        <FrameCalmTitle
          title={t("tileTitle")}
          note={
            query
              ? t("tileMatches", {
                  shown: tileRows,
                  total: orderedTasks.length,
                })
              : String(tileRows)
          }
          action={{
            label: t("openBoard"),
            onClick: () => {
              openFrames({ frameIds: [id], origin: "navigate" });
            },
            testId: "wall-pin-open-board",
          }}
          data-testid="taskboard-frame-top-row"
        />
      ) : opened ? (
        <FrameCalmTitle
          title={title}
          className={CARD_TITLE_INSET}
          data-testid="taskboard-frame-top-row"
        />
      ) : (
        <div
          className="relative z-[80] flex shrink-0 items-center justify-between gap-2 px-3 pt-3"
          data-testid="taskboard-frame-top-row"
        >
          <span
            className="min-w-0 flex-1 truncate text-appkit-body font-semibold text-primary cursor-grab active:cursor-grabbing"
            data-frame-drag-handle="true"
            data-testid="taskboard-frame-title"
          >
            {title}
          </span>
          <FrameChromeTools tools={displayConfig.tools} showLabels />
        </div>
      )}
      {!chromeHidden && (
        <BoardProjectChips
          value={shownProjects}
          onChange={(value) => setBoardProjects(id, value)}
          className={opened ? cn(CARD_TITLE_INSET, "pt-2") : "px-3 pt-2"}
        />
      )}
      {chromeHidden && (
        <TaskboardStatusList
          groups={tileGroups}
          query={query}
          onSelectTask={handleSelectTask}
        />
      )}
      <BoardProjectsContext.Provider value={showsProjects}>
        {!chromeHidden && viewMode === "board" && (
          <div
            className={cn(
              "nodrag flex min-h-0 flex-1 gap-2 overflow-x-auto",
              opened ? OPENED_VIEW_INSET.board : BOARD_INSET,
              inFocus && pocketOpenHere && "pr-[var(--task-pocket-lane)]",
              selected && "nowheel",
            )}
            style={POCKET_LANE_STYLE}
            data-frame-content="true"
            data-kanban-board="true"
          >
            {KANBAN_COLUMNS.map((status) => (
              <KanbanColumn
                key={status}
                status={status}
                tasks={tasksByStatus[status]}
                onSelectTask={handleSelectTask}
                isDragging={isDragging}
                isDropTarget={dropStatus === status}
                onDragStartTask={handleDragStartTask}
                onDragOverStatus={setDropStatus}
                onDragEndTask={handleDragEndTask}
              />
            ))}
          </div>
        )}

        {!chromeHidden && viewMode === "list" && (
          <div
            className={cn(
              "nodrag min-h-0 flex-1 overflow-hidden",
              opened && OPENED_VIEW_INSET.list,
              selected && "nowheel",
            )}
            data-frame-content="true"
          >
            <ListView tasks={tasks} onSelectTask={handleSelectTask} />
          </div>
        )}

        {!chromeHidden && viewMode === "calendar" && (
          <div
            className={cn(
              "nodrag min-h-0 flex-1 overflow-hidden",
              opened && OPENED_VIEW_INSET.calendar,
              selected && "nowheel",
            )}
            data-frame-content="true"
          >
            <CalendarView tasks={tasks} onSelectTask={handleSelectTask} />
          </div>
        )}
      </BoardProjectsContext.Provider>

      {!chromeHidden && (
        <TaskPocketAnchored boardFrameId={id} cardRef={cardRef} />
      )}
    </div>
  );
}
